import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, UserPlus, Trash2, KeyRound, Loader2, UserCog, IdCard } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProfileForm } from "@/components/ProfileForm";
import { ActivityLog } from "@/components/ActivityLog";
import { BrandColors } from "@/components/BrandColors";
import { HeadingFont } from "@/components/HeadingFont";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg, logoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TIER_LABELS } from "@/components/InvoiceDialog";
import { DepositCard } from "@/components/DepositCard";
import { InviteRemindersCard } from "@/components/InviteRemindersCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Card = ({ title, children, testId }) => (
  <section className="rounded-xl border border-slate-800 bg-slate-900/70 p-6 md:p-8" data-testid={testId}>
    <h2 className="font-display text-xl font-semibold tracking-tight mb-6">{title}</h2>{children}
  </section>
);
const L = ({ children }) => <Label className="text-xs uppercase tracking-wider text-slate-400">{children}</Label>;
const field = "bg-slate-950/60 border-slate-700";

const Branding = () => {
  const { settings, setSettings } = useAuth();
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { if (settings && !form) setForm({ company_name: settings.company_name, tagline: settings.tagline || "", currency: settings.currency, notify_email: settings.notify_email || "", tiers: { ...settings.tiers } }); }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!form) return null;

  const save = async () => {
    try { const r = await api.put("/settings", { ...form, notify_email: form.notify_email || null }); setSettings(r.data); toast.success("Settings saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try { const r = await api.post("/settings/logo", fd); setSettings(r.data); toast.success("Logo uploaded"); }
    catch (e) { toast.error(errMsg(e, "Upload failed")); }
    finally { setUploading(false); }
  };

  return (
    <Card title="Branding & pricing" testId="settings-branding">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div className="space-y-2"><L>Company name (PDF cover & invoices)</L><Input data-testid="settings-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className={field} /></div>
          <div className="space-y-2"><L>Tagline (under company name in the menu bar & client portal)</L><Input data-testid="settings-tagline" maxLength={40} placeholder="Feasibility Studies" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={field} /></div>
          <div className="space-y-2"><L>Payment alerts email (staff notified when invoices are paid)</L><Input data-testid="settings-notify-email" type="email" placeholder="billing@yourcompany.com" value={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.value })} className={field} /></div>
          <div className="space-y-2"><L>Invoice currency</L><Input data-testid="settings-currency" maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className={`${field} num w-28`} /></div>
          <div className="space-y-2">
            <L>Logo</L>
            <div className="flex items-center gap-4">
              <div className="h-20 w-40 rounded-lg border border-dashed border-slate-700 bg-white grid place-items-center overflow-hidden">
                {settings.logo_path ? <img data-testid="settings-logo-preview" src={logoUrl(settings.logo_path)} alt="Logo" className="max-h-16 max-w-36 object-contain" /> : <span className="text-xs text-slate-500">No logo</span>}
              </div>
              <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm hover:border-emerald-500">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload
                <input data-testid="settings-logo-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => upload(e.target.files[0])} />
              </label>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <L>Tier prices</L>
          {Object.entries(TIER_LABELS).map(([k, l]) => (
            <div key={k} className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-2.5">
              <span className="text-sm">{l}</span>
              <Input data-testid={`settings-tier-${k}`} type="number" min="0" value={form.tiers[k]} onChange={(e) => setForm({ ...form, tiers: { ...form.tiers, [k]: Number(e.target.value) } })} className={`${field} num w-36 h-9`} />
            </div>
          ))}
        </div>
      </div>
      <Button data-testid="settings-save-btn" onClick={save} className="mt-8 rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">Save settings</Button>
    </Card>
  );
};

const Reminders = () => {
  const { settings, setSettings } = useAuth();
  const [r, setR] = useState(null);
  useEffect(() => { if (settings && !r) setR({ ...settings.reminders }); }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r) return null;
  const num = (k, min, max) => (
    <Input data-testid={`reminder-${k}`} type="number" min={min} max={max} value={r[k]} onChange={(e) => setR({ ...r, [k]: Number(e.target.value) })} className={`${field} num w-24 h-9`} />
  );
  const save = async () => {
    try {
      const res = await api.put("/settings", { company_name: settings.company_name, currency: settings.currency, tiers: settings.tiers, notify_email: settings.notify_email || null, reminders: r });
      setSettings(res.data); toast.success("Reminder settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Overdue invoice reminders" testId="settings-reminders">
      <div className="flex items-center gap-3 mb-6">
        <Switch data-testid="reminder-enabled" checked={r.enabled} onCheckedChange={(v) => setR({ ...r, enabled: v })} />
        <span className="text-sm">{r.enabled ? "Automatic reminders are ON" : "Automatic reminders are OFF"}</span>
      </div>
      <div className={`grid sm:grid-cols-2 gap-4 ${r.enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-2.5"><span className="text-sm">Send at hour (UTC, 0–23)</span>{num("hour_utc", 0, 23)}</div>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-2.5"><span className="text-sm">First reminder (days after due)</span>{num("first_after_days", 0, 90)}</div>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-2.5"><span className="text-sm">Days between reminders</span>{num("gap_days", 1, 90)}</div>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-2.5"><span className="text-sm">Max reminders per invoice</span>{num("max", 1, 10)}</div>
      </div>
      <p className="text-xs text-slate-500 mt-4">Pause reminders for a single invoice with the bell icon on the Invoices page. Current UTC time: {new Date().toISOString().slice(11, 16)}.</p>
      <Button data-testid="reminder-save-btn" onClick={save} className="mt-6 rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">Save reminder settings</Button>
    </Card>
  );
};

const RatingRequests = () => {
  const { settings, setSettings } = useAuth();
  const [r, setR] = useState(null);
  useEffect(() => { if (settings?.rating_requests && !r) setR({ ...settings.rating_requests }); }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r) return null;
  const num = (k, min, max) => (
    <Input data-testid={`rating-${k}`} type="number" min={min} max={max} value={r[k]} onChange={(e) => setR({ ...r, [k]: Number(e.target.value) })} className={`${field} num w-24 h-9`} />
  );
  const save = async () => {
    try {
      const res = await api.put("/settings", { company_name: settings.company_name, currency: settings.currency, tiers: settings.tiers, notify_email: settings.notify_email || null, rating_requests: r });
      setSettings(res.data); toast.success("Rating request settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="“How did we do?” rating requests" testId="settings-rating-requests">
      <div className="flex items-center gap-3 mb-6">
        <Switch data-testid="rating-enabled" checked={r.enabled} onCheckedChange={(v) => setR({ ...r, enabled: v })} />
        <span className="text-sm">{r.enabled ? "Automatic rating requests are ON" : "Automatic rating requests are OFF"}</span>
      </div>
      <div className={`grid sm:grid-cols-2 gap-4 ${r.enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-2.5"><span className="text-sm">Send (days after unlock)</span>{num("first_after_days", 0, 90)}</div>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-2.5"><span className="text-sm">One follow-up after (days)</span>{num("followup_days", 1, 90)}</div>
      </div>
      <p className="text-xs text-slate-500 mt-4">Clients get a one-click sign-in link straight to the rating. Emails go out at the reminder send hour (UTC) and stop once the client rates. You can also send one now from any paid study.</p>
      <Button data-testid="rating-save-btn" onClick={save} className="mt-6 rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">Save rating request settings</Button>
    </Card>
  );
};

const COVERS = [
  ["framed", "Photo framed", "Centered logo, framed property photo, centered title"],
  ["full", "Photo full-width", "Centered logo, edge-to-edge property photo, centered title"],
  ["logo", "Logo only", "Centered logo and title, no photo"],
];

const CoverTemplateCard = () => {
  const { settings, setSettings } = useAuth();
  if (!settings) return null;
  const pick = async (v) => {
    try {
      const res = await api.put("/settings", { company_name: settings.company_name, currency: settings.currency, tiers: settings.tiers, notify_email: settings.notify_email || null, cover_template: v });
      setSettings(res.data); toast.success("Cover layout saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cur = settings.cover_template || "framed";
  return (
    <Card title="PDF & Word cover layout" testId="settings-cover-template">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {COVERS.map(([v, name, desc]) => (
          <button key={v} type="button" data-testid={`cover-template-${v}`} onClick={() => pick(v)}
            className={`text-left rounded-lg border p-4 transition-colors ${cur === v ? "border-emerald-500 bg-emerald-500/10" : "border-slate-800 hover:border-slate-600"}`}>
            <div className="mx-auto mb-3 h-28 w-20 rounded-sm bg-white p-1.5 flex flex-col items-center gap-1">
              <span className="h-2 w-8 rounded-sm bg-slate-400" />
              {v !== "logo" && <span className={`${v === "full" ? "w-full h-9" : "w-12 h-7 border border-black"} bg-slate-300`} />}
              <span className="mt-auto h-1.5 w-12 rounded-sm bg-slate-700" /><span className="h-1 w-9 rounded-sm bg-slate-400" />
              <span className="mt-2 h-px w-full bg-black" />
            </div>
            <p className="text-sm font-semibold">{name}{cur === v && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-400">Active</span>}</p>
            <p className="text-xs text-slate-400">{desc}</p>
          </button>
        ))}
      </div>
    </Card>
  );
};

const RepeatOfferCard = () => {
  const { settings, setSettings } = useAuth();
  const [r, setR] = useState(null);
  useEffect(() => { if (settings?.repeat_offer && !r) setR({ ...settings.repeat_offer }); }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!r) return null;
  const num = (k, min, max) => (
    <Input data-testid={`offer-${k}`} type="number" min={min} max={max} value={r[k]} onChange={(e) => setR({ ...r, [k]: Number(e.target.value) })} className={`${field} num w-24 h-9`} />
  );
  const save = async () => {
    try {
      const res = await api.put("/settings", { company_name: settings.company_name, currency: settings.currency, tiers: settings.tiers, notify_email: settings.notify_email || null, repeat_offer: r });
      setSettings(res.data); toast.success("Repeat business offer saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Repeat business offer" testId="settings-repeat-offer">
      <div className="flex items-center gap-3 mb-6">
        <Switch data-testid="offer-enabled" checked={r.enabled} onCheckedChange={(v) => setR({ ...r, enabled: v })} />
        <span className="text-sm">{r.enabled ? "Offer shown after 4 and 5-star ratings" : "Offer is OFF"}</span>
      </div>
      <div className={`grid sm:grid-cols-2 gap-4 ${r.enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-2.5"><span className="text-sm">Discount (%)</span>{num("percent", 1, 90)}</div>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-2.5"><span className="text-sm">Valid for (days)</span>{num("valid_days", 1, 365)}</div>
      </div>
      <p className="text-xs text-slate-500 mt-4">The discount is applied automatically to the client's next invoice, once, while the offer is valid.</p>
      <Button data-testid="offer-save-btn" onClick={save} className="mt-6 rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">Save offer settings</Button>
    </Card>
  );
};

const Staff = () => {
  const { user, settings } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/users", form); toast.success("Staff account created"); setForm({ name: "", email: "", password: "", role: "staff" }); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const reset = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 8 characters)`);
    if (!pw) return;
    try { await api.put(`/users/${u.id}/password`, { password: pw }); toast.success("Password updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const impersonate = async (u) => {
    try { await api.post(`/admin/impersonate/${u.id}`); window.location.href = "/studies"; }
    catch (e) { toast.error(errMsg(e, "Could not impersonate")); }
  };
  return (
    <Card title="Staff accounts" testId="settings-staff">
      <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <Input data-testid="staff-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
        <Input data-testid="staff-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
        <Input data-testid="staff-password" required minLength={8} type="password" placeholder="Temp password (8+)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} />
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger data-testid="staff-role" className={field}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700"><SelectItem value="staff">Staff</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
        </Select>
        <Button data-testid="staff-add-btn" className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400"><UserPlus className="h-4 w-4 mr-1" />Add</Button>
      </form>
      <div className="divide-y divide-slate-800 rounded-lg border border-slate-800">
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between gap-2 sm:gap-4 px-4 py-3" data-testid={`staff-row-${u.id}`}>
            <div className="min-w-0"><p className="text-sm font-semibold">{u.name} <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">{u.role}</span></p><p className="text-xs text-slate-400 break-all">{u.title ? `${u.title} · ` : ""}{u.email}</p></div>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" title="Edit title and photo" data-testid={`staff-profile-${u.id}`} onClick={() => setEditing(u)}><IdCard className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" title="Reset password" data-testid={`staff-reset-${u.id}`} onClick={() => reset(u)}><KeyRound className="h-4 w-4" /></Button>
              {u.role !== "admin" && !user.impersonator && <Button size="sm" variant="outline" title="Sign in as this user for up to 1 hour" data-testid={`staff-impersonate-${u.id}`} onClick={() => impersonate(u)} className="h-8 rounded-full border-amber-400/40 bg-transparent text-amber-300 hover:bg-amber-400/10"><UserCog className="h-4 w-4 mr-1" />Impersonate</Button>}
              {u.id !== user.id && <Button size="icon" variant="ghost" title="Remove" data-testid={`staff-delete-${u.id}`} className="hover:text-rose-400" onClick={() => remove(u)}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl" data-testid="staff-profile-dialog">
          <DialogHeader><DialogTitle className="font-display">Signature for {editing?.name}</DialogTitle></DialogHeader>
          {editing && <ProfileForm user={editing} company={settings?.company_name || ""} onSaved={() => load()} />}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default function Settings() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8 fade-up">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Admin</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Settings</h1>
      </div>
      <Branding />
      <Reminders />
      <RatingRequests />
      <RepeatOfferCard />
      <DepositCard Card={Card} />
      <InviteRemindersCard Card={Card} />
      <CoverTemplateCard />
      <BrandColors Card={Card} />
      <HeadingFont Card={Card} />
      <Staff />
      <ActivityLog Card={Card} />
    </div>
  );
}
